import UploadAdapter, { progressEventType } from './upload/UploadAdapter';
import { merge, defer, Subject, EMPTY } from 'rxjs';
import { filter, map, mergeMap, share } from 'rxjs/operators';
import params$, { paramErrors$ } from './upload/rx/params';
import smallThumbs$, { smallThumbErrors$ } from './upload/rx/smallThumbs';
import largeThumbs$, { largeThumbErrors$ } from './upload/rx/largeThumbs';
import watermark$, { watermarkErrors$ } from './upload/rx/watermark';
import uploadTicket$, { uploadTicketErrors$ } from './upload/rx/uploadTicket';
import uploadReq$ from './upload/rx/uploadReq';
import hashes$, { hashesErrors$ } from './upload/rx/hashes';
import zipForUpload from './upload/rx/zipForUpload';
import convertErrors from './upload/rx/convertErrors';
import errorsOnly from './upload/rx/errorsOnly';
import noErrors from './upload/rx/noErrors';
import Session from '@/model/Session';
import UploadedPhoto from '@/model/UploadedPhoto';
import ImageProcessingError from '@/model/ImageProcessingError';
import ids$, { conflicts$, idErrors$ } from './upload/rx/ids';
import lifecycleSignal$, { lifecycleSubject } from './upload/rx/lifecycleSignal';
import singles$, { findUpload } from './upload/rx/singles';
import sessionGuard from '@/service/sessionGuard';

const uploadServices: { [name: string]: UploadAdapter } = {};
const errors$ = merge(paramErrors$, smallThumbErrors$, largeThumbErrors$, hashesErrors$,
    uploadTicketErrors$, watermarkErrors$, idErrors$);
const progress$ = new Subject<{ uploaded: number; eventType: progressEventType; overall: number; photoId: string }>();
const completionErrors$ = new Subject<ImageProcessingError<unknown>>();
const discarded$ = lifecycleSignal$.pipe(map(({ photo }) => photo));

function end(photo: UploadedPhoto, lifecycleEvent: 'finish' | 'error' | 'cancel') {
    if (!photo.active) return;
    photo.finished = true;
    if (lifecycleEvent !== 'finish') photo.controller.abort();
    lifecycleSubject.next({ photo, file: photo.file, lifecycleEvent });
}

const uploads$ = zipForUpload({
    params: params$, smallThumb: smallThumbs$, largeThumb: largeThumbs$,
    watermarked: watermark$, hashes: hashes$, uploadTicket: uploadTicket$, ids: ids$,
}, ({ photo }) => photo, errors$, discarded$).pipe(
    mergeMap(zipped => {
        if (!zipped.photo.active) return EMPTY;
        let lastProgress = 0;
        return defer(() => uploadServices[zipped.ticket.serviceName].uploadFile(zipped.ticket, {
            file: zipped.file, sessionId: zipped.photo.sessionId, id: zipped.id,
            overwrite: zipped.overwrite, signal: zipped.photo.signal,
            thumbnails: { smallThumb: zipped.smallThumb, largeThumb: zipped.watermarked, noWatermark: zipped.largeThumb },
        }, (uploaded, overall, eventType) => {
            if (!zipped.photo.active) return;
            const now = performance.now();
            if (eventType === 'progress') {
                if (now - lastProgress < 100) return;
                lastProgress = now;
            }
            sessionGuard.touch();
            progress$.next({ uploaded, overall, photoId: zipped.id, eventType });
        })).pipe(
            map(result => ({ ...zipped, ...result })),
            convertErrors(zipped.photo, 'upload'),
            filter(() => zipped.photo.active),
        );
    }, 4),
    share(),
);
const allErrors$ = merge(errors$, uploads$.pipe(errorsOnly()), completionErrors$).pipe(share());
allErrors$.subscribe(error => end(error.photo, 'error'));
uploads$.subscribe();

export default {
    addUploadService(service: UploadAdapter) { uploadServices[service.name] = service; },
    uploadFiles(session: Session, files: Array<{ file: File; id?: string }>) {
        if (!session.watermark) throw new Error('Session has unresolved watermark.');
        sessionGuard.touch();
        uploadReq$.next({ sessionId: session.id, finalizationMode: session.finalizationMode, files, watermark: session.watermark });
    },
    async cancel(file: File) {
        sessionGuard.touch();
        const photo = findUpload(file);
        if (photo) end(photo, 'cancel');
    },
    finish(photo: UploadedPhoto) { end(photo, 'finish'); },
    fail(photo: UploadedPhoto, error: unknown) {
        if (photo.active) completionErrors$.next(new ImageProcessingError(photo, error, EMPTY, 'savePhoto'));
    },
    singles$, ids$, params$, smallThumbs$, hashes$, progress$, watermark$,
    conflicts$: zipForUpload({ single: singles$, watermarked: watermark$, smallThumb: smallThumbs$, conflicts: conflicts$ },
        ({ photo }) => photo, errors$, discarded$),
    uploads$: uploads$.pipe(noErrors()),
    errors$: allErrors$,
};
